import React, { useState, useEffect, useCallback } from 'react';
import { useGame, api, useLiveCoins } from '../context/GameContext';
import { useNavigate } from 'react-router-dom';
import { Coins, Wrench, TrendingUp, Shield, Clock, Star, Image, User, Timer } from 'lucide-react';
import GameTooltip from '../components/GameTooltip';
import ChessRook from '../components/ChessRook';
import LiveCoinPill from '../components/LiveCoinPill';
import SectionLoader from '../components/SectionLoader';

const TIER_COLORS = [
  '#52525B', // tier 0 (unfilled) - gray
  '#9CA3AF', // tier 1 - silver
  '#3B82F6', // tier 2 - blue
  '#10B981', // tier 3 - green
  '#F59E0B', // tier 4 - amber
  '#EF4444', // tier 5 - red
  '#A855F7', // tier 6 - purple
  '#EC4899', // tier 7 - pink
  '#D4AF37', // tier 8 - gold
  '#F5F5F7', // tier 9 - white/platinum
  '#D4AF37', // tier 10 - max gold
];

const UPGRADE_META = {
  discover_reward: {
    icon: Star,
    en: { name: 'Discovery Bonus', desc: 'Painting discovery rewards' },
    ru: { name: 'Бонус открытий', desc: 'Награда за открытие картин' },
    es: { name: 'Bono de descubrimiento', desc: 'Recompensa por descubrir pinturas' },
    ar: { name: 'مكافاة الاكتشاف', desc: 'مكافاة اكتشاف اللوحات' },
    uk: { name: 'Бонус вiдкриттiв', desc: 'Винагорода за вiдкриття картин' },
    fr: { name: 'Bonus de decouverte', desc: 'Recompenses de decouverte de peinture' },
    pt: { name: 'Bonus de descoberta', desc: 'Recompensas de descoberta de pinturas' },
    zh: { name: '发现奖励', desc: '画作发现奖励' },
    unit: '+10%',
  },
  exhibit_income: {
    icon: TrendingUp,
    en: { name: 'Exhibit Income', desc: 'Income from exhibitions' },
    ru: { name: 'Доход выставок', desc: 'Доход от картин на выставках' },
    es: { name: 'Ingresos de exposicion', desc: 'Ingresos de exposiciones' },
    ar: { name: 'دخل المعرض', desc: 'دخل من المعارض' },
    uk: { name: 'Дохiд виставок', desc: 'Дохiд вiд картин на виставках' },
    fr: { name: 'Revenus d\'exposition', desc: 'Revenus des expositions' },
    pt: { name: 'Renda de exposicao', desc: 'Renda das exposicoes' },
    zh: { name: '展览收入', desc: '展览收入' },
    unit: '+1%',
  },
  influence_income: {
    icon: TrendingUp,
    en: { name: 'Influence Income', desc: 'Influence gain from exhibitions' },
    ru: { name: 'Доход влияния', desc: 'Прирост влияния от выставок' },
    es: { name: 'Ingreso de influencia', desc: 'Ganancia de influencia de exposiciones' },
    ar: { name: 'دخل التاثير', desc: 'كسب التاثير من المعارض' },
    uk: { name: 'Дохiд впливу', desc: 'Приростання впливу вiд виставок' },
    fr: { name: 'Revenus d\'influence', desc: 'Gain d\'influence des expositions' },
    pt: { name: 'Renda de influencia', desc: 'Ganho de influencia de exposicoes' },
    zh: { name: '影响力收入', desc: '展览影响力收入' },
    unit: '+1%',
  },
  buy_discount: {
    icon: Coins,
    en: { name: 'Buy Discount', desc: 'Random painting price' },
    ru: { name: 'Скидка покупки', desc: 'Цена за случайную картину' },
    es: { name: 'Descuento de compra', desc: 'Precio de pintura aleatoria' },
    ar: { name: 'خصم الشراء', desc: 'سعر اللوحة العشوائية' },
    uk: { name: 'Знижка покупки', desc: 'Цiна випадкової картини' },
    fr: { name: 'Remise d\'achat', desc: 'Prix peinture aleatoire' },
    pt: { name: 'Desconto de compra', desc: 'Preco de pintura aleatoria' },
    zh: { name: '购买折扣', desc: '随机画作价格' },
    unit: '-1%',
  },
  repair_discount: {
    icon: Wrench,
    en: { name: 'Repair Discount', desc: 'Repair cost reduction' },
    ru: { name: 'Скидка починки', desc: 'Починка картин дешевле' },
    es: { name: 'Descuento de reparacion', desc: 'Reduccion del costo de reparacion' },
    ar: { name: 'خصم الاصلاح', desc: 'تخفيض تكلفة الاصلاح' },
    uk: { name: 'Знижка ремонту', desc: 'Зниження вартостi ремонту' },
    fr: { name: 'Remise de reparation', desc: 'Reduction du cout de reparation' },
    pt: { name: 'Desconto de reparo', desc: 'Reducao do custo de reparo' },
    zh: { name: '修复折扣', desc: '修复成本降低' },
    unit: '-1%',
  },
  durability_slow: {
    icon: Shield,
    en: { name: 'Durability', desc: 'Slower painting wear' },
    ru: { name: 'Износостойкость', desc: 'Износ картин медленнее' },
    es: { name: 'Durabilidad', desc: 'Desgaste de pintura mas lento' },
    ar: { name: 'المتانة', desc: 'تاكل اللوحة ابطا' },
    uk: { name: 'Зносостiйкiсть', desc: 'Знос картин повiльнiше' },
    fr: { name: 'Durabilite', desc: 'Usure de peinture plus lente' },
    pt: { name: 'Durabilidade', desc: 'Desgaste de pintura mais lento' },
    zh: { name: '耐久性', desc: '画作磨损减缓' },
    unit: '-1%',
  },
  exhibit_slots: {
    icon: Clock,
    en: { name: 'Exhibit Slots', desc: 'Exhibit simultaneously' },
    ru: { name: 'Слоты выставок', desc: 'Одновременно выставлять' },
    es: { name: 'Ranuras de exposicion', desc: 'Exponer simultaneamente' },
    ar: { name: 'فتحات المعرض', desc: 'العرض في وقت واحد' },
    uk: { name: 'Слоти виставок', desc: 'Виставляти одночасно' },
    fr: { name: 'Emplacements d\'exposition', desc: 'Exposer simultanement' },
    pt: { name: 'Slots de exposicao', desc: 'Expor simultaneamente' },
    zh: { name: '展览位', desc: '同时展出' },
    unit: '+1',
  },
  painting_slots: {
    icon: Image,
    en: { name: 'Painting Slots', desc: 'Available inventory slots' },
    ru: { name: 'Слоты картин', desc: 'Доступные слоты для картин' },
    es: { name: 'Ranuras de pintura', desc: 'Ranuras de inventario disponibles' },
    ar: { name: 'فتحات اللوحات', desc: 'فتحات المخزون المتاحة' },
    uk: { name: 'Слоти картин', desc: 'Доступнi слоти для картин' },
    fr: { name: 'Emplacements de peinture', desc: 'Emplacements d\'inventaire disponibles' },
    pt: { name: 'Slots de pintura', desc: 'Slots de inventario disponiveis' },
    zh: { name: '画作位', desc: '可用画作槽位' },
    unit: '+1',
  },
  exhibit_duration: {
    icon: Timer,
    en: { name: 'Exhibit Time', desc: '+10 min per single exhibit' },
    ru: { name: 'Время выставки', desc: '+10 минут к одной выставке' },
    es: { name: 'Tiempo de exposicion', desc: '+10 min por exposicion' },
    ar: { name: 'وقت المعرض', desc: '+10 دقائق لكل معرض' },
    uk: { name: 'Час виставки', desc: '+10 хв до однiєi виставки' },
    fr: { name: 'Duree d\'exposition', desc: '+10 min par exposition' },
    pt: { name: 'Tempo de exposicao', desc: '+10 min por exposicao' },
    zh: { name: '展览时间', desc: '每次展览 +10 分钟' },
    unit: '+10m',
    isNew: true,
  },
};

const TierCells = ({ level, maxLevel }) => {
  const totalTiers = Math.ceil(maxLevel / 10);
  const currentTier = Math.floor(level / 10); // completed tiers
  const cellsInCurrentTier = level % 10;

  const cells = [];
  for (let i = 0; i < 10; i++) {
    const filled = i < cellsInCurrentTier || (currentTier >= totalTiers && level >= maxLevel);
    const tierColor = TIER_COLORS[Math.min(currentTier + 1, TIER_COLORS.length - 1)];
    cells.push(
      <div
        key={i}
        className="h-2 rounded-sm transition-all duration-300"
        style={{
          flex: 1,
          backgroundColor: filled ? tierColor : '#1a1a2e',
          boxShadow: filled ? `0 0 4px ${tierColor}44` : 'none',
        }}
      />
    );
  }

  const completedTierColor = currentTier > 0 ? TIER_COLORS[Math.min(currentTier, TIER_COLORS.length - 1)] : null;

  return (
    <div>
      <div className="flex gap-0.5">
        {cells}
      </div>
      <div className="flex items-center justify-between mt-0.5">
        <span className="text-[8px] font-mono" style={{ color: completedTierColor || '#52525B' }}>
          {currentTier > 0 ? `Tier ${currentTier}` : ''}
        </span>
        <span className="text-[8px] font-mono text-[#52525B]">
          {level}/{maxLevel}
        </span>
      </div>
    </div>
  );
};

const UpgradesPage = () => {
  const { user, setUser, refreshUser, language, haptic, tutorialStep, setTutorialStep, t, flashCoinReward, consumePrefetch } = useGame();
  const liveCoins = useLiveCoins();
  const navigate = useNavigate();
  // Boot-time prefetch: GameContext already fired /upgrades/me in
  // parallel with auth, so we render real data on first mount instead
  // of waiting for the round-trip. `consumePrefetch` is single-use;
  // subsequent visits fall back to fresh fetch.
  const bootUpgrades = React.useMemo(
    () => (consumePrefetch ? consumePrefetch('/upgrades/me') : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [upgrades, setUpgrades] = useState(bootUpgrades?.upgrades || []);
  const [loading, setLoading] = useState(!bootUpgrades);
  const [buying, setBuying] = useState(null);
  const [maxPaintings, setMaxPaintings] = useState(bootUpgrades?.max_paintings || 2);
  const [maxExhibits, setMaxExhibits] = useState(bootUpgrades?.max_exhibits || 1);

  // Coin pill animation now lives inside <LiveCoinPill /> — explicit
  // purchase rewards route through the context flash bus.
  const triggerCoinReward = useCallback((amount, opts = {}) => {
    flashCoinReward(amount, opts);
  }, [flashCoinReward]);

  const loadUpgrades = async () => {
    if (!user?.id) return;
    try {
      const res = await api.get(`/upgrades/me`);
      setUpgrades(res.data.upgrades);
      setMaxPaintings(res.data.max_paintings);
      setMaxExhibits(res.data.max_exhibits);
      // Sync the authoritative coin balance from the response — the
      // backend just drip-committed any pending exhibit income for us
      // and returns the fresh total in `res.data.coins`. Without this
      // the context's `user.coins` stayed stale, so `liveCoins` on
      // Upgrades undercounted relative to Gallery (which refreshes
      // the user object via /cards/me's sibling /users/me call).
      if (typeof res.data?.coins === 'number' && setUser) {
        setUser(prev => prev ? { ...prev, coins: res.data.coins } : prev);
      }
    } catch (err) {
      console.error('Error loading upgrades:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUpgrades(); }, [user?.id]);

  const handleBuy = async (key, isFree = false) => {
    if (buying) return;
    haptic && haptic();
    setBuying(key);
    try {
      const freeParam = isFree ? '&free=true' : '';
      const res = await api.post(`/upgrades/me/buy?upgrade_key=${key}${freeParam}`);
      // Optimistic UI: the response already carries every field the
      // page needs (new level, coins remaining, max_paintings,
      // max_exhibits). Apply locally instead of firing two extra
      // round-trips to /users/me and /upgrades/me — they collectively
      // added ~300-400ms on mobile networks and made the upgrade feel
      // sluggish. Subsequent navigation will refresh authoritative
      // state naturally.
      const newCoins = res.data?.coins_remaining;
      const newLevel = res.data?.new_level;
      const newCost = res.data?.next_cost;  // null = max level reached
      const maxLevelReached = !!res.data?.max_level_reached;
      const nextMaxPaintings = res.data?.max_paintings;
      const nextMaxExhibits = res.data?.max_exhibits;
      // 1) Patch upgrades list in place (level + backend-computed next
      //    cost). When the upgrade hits max level, `next_cost` is null
      //    and we propagate that so the UI locks the buy button.
      setUpgrades(prev => prev.map(up => up.key === key ? {
        ...up,
        level: typeof newLevel === 'number' ? newLevel : (up.level + 1),
        cost: maxLevelReached ? null : (newCost ?? up.cost),
      } : up));
      // 2) Sync the slot caps if this upgrade changed them.
      if (typeof nextMaxPaintings === 'number') setMaxPaintings(nextMaxPaintings);
      if (typeof nextMaxExhibits === 'number') setMaxExhibits(nextMaxExhibits);
      // 3) Patch the user context with EVERY field other screens
      //    read after an upgrade. Coins keeps the balance pill in
      //    sync. max_paintings / max_exhibits MUST be patched too —
      //    GalleryPage reads `user?.max_paintings` straight from the
      //    context to decide whether the "buy painting" button is
      //    enabled. Without this the player gets bumped to upgrade,
      //    upgrades the slots, returns to gallery, and still sees
      //    "no slot" until the next periodic /users/me refresh
      //    catches up — the 1-2 minute lag tutorial users were
      //    reporting after the optimistic-UI rewrite shipped.
      if (setUser) {
        setUser(prev => {
          if (!prev) return prev;
          const next = { ...prev };
          if (typeof newCoins === 'number') next.coins = newCoins;
          if (typeof nextMaxPaintings === 'number') next.max_paintings = nextMaxPaintings;
          if (typeof nextMaxExhibits === 'number') next.max_exhibits = nextMaxExhibits;
          return next;
        });
      }
      // (Coin pill animation lives inside <LiveCoinPill /> now — the
      // balance number updates via the context patch above.)
      // Tutorial: after upgrading painting_slots, go to gallery
      if (tutorialStep === 'upgrade_slots' && key === 'painting_slots') {
        setTutorialStep('gallery');
      }
    } catch (err) {
      const msg = err.response?.data?.detail;
      alert(msg || 'Error');
      // Fall back to a fresh load only on error so the UI is never
      // stuck on a wrong state when something failed mid-flight.
      try { await loadUpgrades(); } catch (_) { /* noop */ }
    } finally {
      setBuying(null);
    }
  };

  const lang = language || 'en';

  if (loading) {
    return <SectionLoader progress={0} language={language} />;
  }

  return (
    <div className="pb-24 px-4" data-testid="upgrades-page">
      {/* Header — sticky */}
      <div className="sticky top-0 z-40 bg-[#05050A] pt-4 pb-3 -mx-4 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GameTooltip text={lang === 'ru' ? 'Ваши монеты' : 'Your coins'} position="bottom">
              <LiveCoinPill />
            </GameTooltip>
            <GameTooltip text={t('total_influence')} position="bottom">
              <div className="flex items-center gap-1.5 glass-card px-2.5 py-1.5 rounded-full cursor-pointer">
                <ChessRook className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span className="font-mono text-xs tabular-nums">{user?.total_rating || 0}</span>
              </div>
            </GameTooltip>
            <GameTooltip text={lang === 'ru' ? 'Слоты для картин' : 'Painting slots'} position="bottom">
              <div className="flex items-center gap-1.5 glass-card px-2.5 py-1.5 rounded-full cursor-pointer">
                <Image className="w-3.5 h-3.5 text-[#A1A1AA]" />
                <span className="font-mono text-xs tabular-nums">{user?.cards_count || 0}/{maxPaintings}</span>
              </div>
            </GameTooltip>
          </div>
          <button
            data-testid="profile-btn-upgrades"
            onClick={() => { haptic(); navigate('/profile'); }}
            className="w-8 h-8 rounded-full glass-card border border-white/10 flex items-center justify-center hover:border-[#D4AF37]/50 transition-colors"
          >
            {user?.photo_url
              ? <img src={user.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
              : <User className="w-4 h-4 text-[#A1A1AA]" />
            }
          </button>
        </div>
      </div>

      <div className="space-y-2 mt-1">
        {upgrades.map((up) => {
          const meta = UPGRADE_META[up.key];
          if (!meta) return null;
          const Icon = meta.icon;
          const info = meta[lang] || meta.en;
          const isTutorialSlots = tutorialStep === 'upgrade_slots' && up.key === 'painting_slots';
          const isFreeFirst = up.key === 'painting_slots' && up.level === 0;
          // While the player still hasn't completed the "buy your
          // second painting" milestone we only let them purchase the
          // FIRST (free) level of `painting_slots`. Otherwise it's too
          // easy to either dump all starting coins into a different
          // upgrade and get stuck without enough left for the second
          // slot, OR to repeatedly buy further painting_slots levels
          // and burn the same coins.
          const inEarlyTutorial = !!tutorialStep && (user?.cards_count || 0) < 2;
          const slotsAlreadyUnlocked = up.key === 'painting_slots' && up.level >= 1;
          const lockedByTutorial = inEarlyTutorial && (up.key !== 'painting_slots' || slotsAlreadyUnlocked);
          // Use `liveCoins` (= user.coins + un-synced exhibit drip)
          // instead of the stale `user.coins` so the affordability
          // check matches the balance pill the player sees ticking up.
          // Backend re-runs `apply_partial_exhibit_commit` on the
          // actual /upgrades/me/buy request, so a "yes you can afford
          // this" here will always be honored server-side.
          const availableCoins = liveCoins ?? (user?.coins || 0);
          const canBuy = !lockedByTutorial && (isFreeFirst || (up.cost && availableCoins >= up.cost));
          const isMax = up.level >= up.max_level;
          const isBuying = buying === up.key;
          const currentTier = Math.floor(up.level / 10);
          const tierColor = currentTier > 0 ? TIER_COLORS[Math.min(currentTier, TIER_COLORS.length - 1)] : null;

          return (
            <div
              key={up.key}
              data-testid={`upgrade-${up.key}`}
              className={`glass-card rounded-xl p-3 transition-all duration-300 ${isTutorialSlots ? 'tut-halo' : ''} ${lockedByTutorial ? 'opacity-45 saturate-50' : ''}`}
              style={tierColor ? {
                borderLeft: `3px solid ${tierColor}`,
                boxShadow: `inset 2px 0 8px ${tierColor}15`,
              } : undefined}
            >
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors"
                  style={{
                    backgroundColor: tierColor ? `${tierColor}20` : '#D4AF3718',
                    border: tierColor ? `1px solid ${tierColor}33` : undefined,
                  }}
                >
                  {meta.useChessRook
                    ? <ChessRook className="w-4.5 h-4.5" style={{ color: '#D4AF37' }} />
                    : <Icon className="w-4.5 h-4.5" style={{ color: tierColor || '#D4AF37' }} />
                  }
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-medium text-[#F5F5F7] truncate">{info.name}</span>
                    {meta.isNew && up.level === 0 && (
                      <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-[#10B981] text-white tracking-wider shrink-0">
                        NEW
                      </span>
                    )}
                    <span className="text-[10px] font-mono" style={{ color: tierColor || '#D4AF37' }}>
                      Lv.{up.level}
                    </span>
                  </div>
                  <p className="text-[10px] text-[#A1A1AA] truncate">
                    {info.desc} {up.level > 0 && <span style={{ color: tierColor || '#D4AF37' }}>{meta.unit.startsWith('-') ? '-' : '+'}{up.level * (meta.unit.includes('10') ? 10 : 1)}{meta.unit.includes('%') ? '%' : (meta.unit.includes('m') ? 'm' : '')}</span>}
                  </p>
                </div>

                {isMax ? (
                  <span className="text-[10px] text-[#10B981] font-medium px-2 py-1 rounded bg-[#10B981]/10 shrink-0">MAX</span>
                ) : lockedByTutorial ? (
                  <span
                    data-testid={`upgrade-${up.key}-locked`}
                    className="text-[9px] text-[#A1A1AA] font-medium px-2 py-1 rounded bg-[#1F1F2A] border border-[#3F3F46] shrink-0 uppercase tracking-wider"
                  >
                    {lang === 'ru' ? 'после туториала' : 'after tutorial'}
                  </span>
                ) : (
                  <button
                    onClick={() => handleBuy(up.key, isFreeFirst)}
                    disabled={(!canBuy || isBuying) && !isTutorialSlots}
                    className={`shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg border disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95 ${isTutorialSlots ? 'tut-halo' : ''}`}
                    style={canBuy ? {
                      backgroundColor: '#10B98120',
                      borderColor: isTutorialSlots ? '#F5D07A' : '#10B98155',
                    } : {
                      backgroundColor: tierColor ? `${tierColor}15` : '#D4AF3715',
                      borderColor: tierColor ? `${tierColor}44` : '#D4AF3744',
                    }}
                  >
                    {isBuying ? (
                      <div className="w-3 h-3 border-2 border-[#10B981] border-t-transparent rounded-full animate-spin" />
                    ) : isFreeFirst ? (
                      <span className="font-mono text-[11px] font-bold" style={{ color: '#10B981' }}>FREE</span>
                    ) : (
                      <>
                        <Coins className="w-3 h-3" style={{ color: canBuy ? '#10B981' : (tierColor || '#D4AF37') }} />
                        <span className="font-mono text-[11px]" style={{ color: canBuy ? '#10B981' : (tierColor || '#D4AF37') }}>{up.cost}</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              <TierCells level={up.level} maxLevel={up.max_level} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UpgradesPage;
